import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  Search,
  ChevronRight,
  ChevronLeft,
  X,
  Maximize2,
  Minimize2,
  BookOpen,
  Star,
  User,
  Calendar,
  Clock,
  ThumbsUp,
  ThumbsDown,
  Meh,
  Eye,
} from "lucide-react";
import * as FaIcons from "react-icons/fa";
import DOMPurify from "dompurify";
import { kbService } from "../utils/kbService";
import { getFallbackBackView } from "../utils/navigationHelper";

function Help({
  setView,
  setActiveIcon,
  setupDetails,
  setIsOpen,
  isExpanded,
  setIsExpanded,
  widgetConfigs,
  selectedArticle,
  setSelectedArticle,
}) {
  const partnerId =
    widgetConfigs?.uniquePartnerId ||
    setupDetails?.uniquePartnerId ||
    localStorage.getItem("uniquePartnerId");

  // View modes: 'home' | 'collection' | 'article'
  const [mode, setMode] = useState(selectedArticle ? "article" : "home");
  const [currentArticle, setCurrentArticle] = useState(selectedArticle || null);
  const [currentCollection, setCurrentCollection] = useState(null);

  // Data states
  const [collections, setCollections] = useState([]);
  const [frequentlyReadArticles, setFrequentlyReadArticles] = useState([]);
  const [allPublishedArticles, setAllPublishedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [articleLoading, setArticleLoading] = useState(false);

  // Search states
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Feedback & Image zoom states
  const [feedbackGiven, setFeedbackGiven] = useState(null);
  const [zoomedImage, setZoomedImage] = useState(null);

  const scrollRef = useRef(null);
  const articleContentRef = useRef(null);

  // Custom colors
  const backgroundColor =
    setupDetails?.widgetCustomizations?.backgroundColor || "#FFFFFF";
  const primaryColor =
    setupDetails?.widgetCustomizations?.color2 ||
    setupDetails?.widgetCustomizations?.uiColor ||
    setupDetails?.widgetCustomizations?.color1 ||
    "#2563eb";

  // Helper for primary color with opacity
  const primaryShade = (opacity) => {
    const hex = primaryColor.replace("#", "");
    if (hex.length === 6) {
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
    return `rgba(37, 99, 235, ${opacity})`;
  };

  // Open an article and load full content if missing
  const openArticle = useCallback(
    async (art) => {
      setMode("article");
      setFeedbackGiven(null);
      setSearchQuery("");

      // If article already has full text
      if (
        art?.content?.text ||
        (typeof art?.content === "string" && art?.content.length > 0)
      ) {
        setCurrentArticle(art);
      } else {
        setArticleLoading(true);
        setCurrentArticle(art);
        try {
          const fullArt = art._id
            ? await kbService.getArticleById(art._id)
            : await kbService.getArticleBySlug(partnerId, art.slug);
          if (fullArt) setCurrentArticle(fullArt);
        } catch (err) {
          console.error("Failed to load article detail:", err);
        } finally {
          setArticleLoading(false);
        }
      }

      // Find article's collection for breadcrumbs
      if (art?.parentCollection) {
        const parentId = Array.isArray(art.parentCollection)
          ? art.parentCollection[0]?._id || art.parentCollection[0]
          : art.parentCollection?._id || art.parentCollection;
        const foundCol = collections.find((c) => c._id === parentId);
        if (foundCol) setCurrentCollection(foundCol);
      }

      // Track view
      const targetId = art?._id || art?.id;
      if (targetId && partnerId) {
        kbService.trackView(targetId, partnerId);
      }

      // Scroll top
      if (scrollRef.current) {
        scrollRef.current.scrollTop = 0;
      }
    },
    [collections, partnerId]
  );

  // Auto-expand on article view, auto-collapse on collection/home view
  useEffect(() => {
    if (mode === "article") {
      if (setIsExpanded) setIsExpanded(true);
    } else {
      if (setIsExpanded) setIsExpanded(false);
    }
  }, [mode, setIsExpanded]);

  // Clean up when unmounting Help component (e.g. user navigates to another tab or closes)
  useEffect(() => {
    return () => {
      if (setIsExpanded) setIsExpanded(false);
      if (setSelectedArticle) setSelectedArticle(null);
    };
  }, [setIsExpanded, setSelectedArticle]);

  // Sync with selectedArticle prop from LandingWindow
  useEffect(() => {
    if (selectedArticle) {
      openArticle(selectedArticle);
    }
  }, [selectedArticle, openArticle]);

  // Initial fetch of collections, most-viewed articles, and published articles
  useEffect(() => {
    if (!partnerId) {
      setLoading(false);
      return;
    }
    let isMounted = true;
    setLoading(true);

    Promise.all([
      kbService.getCollections(partnerId),
      kbService.getMostViewedArticles(partnerId, 6),
      kbService.getPublishedArticles(partnerId),
    ])
      .then(([cols, mostViewed, published]) => {
        if (!isMounted) return;
        setCollections(Array.isArray(cols) ? cols : []);
        setAllPublishedArticles(Array.isArray(published) ? published : []);

        if (Array.isArray(mostViewed) && mostViewed.length > 0) {
          setFrequentlyReadArticles(mostViewed.slice(0, 6));
        } else {
          // Fallback to published or featured
          kbService.getFeaturedArticles(partnerId).then((feat) => {
            if (isMounted) {
              if (Array.isArray(feat) && feat.length > 0) {
                setFrequentlyReadArticles(feat.slice(0, 6));
              } else if (Array.isArray(published)) {
                setFrequentlyReadArticles(published.slice(0, 6));
              }
            }
          });
        }
      })
      .catch((err) => console.error("Error loading Knowledge Base data:", err))
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [partnerId]);

  // Live article search with debounce
  useEffect(() => {
    if (!partnerId || !searchQuery || searchQuery.trim().length < 2) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    const handler = setTimeout(() => {
      kbService
        .searchArticles(partnerId, searchQuery)
        .then((res) => {
          setSearchResults(Array.isArray(res) ? res : []);
        })
        .catch((err) => {
          console.error("Search error:", err);
          setSearchResults([]);
        })
        .finally(() => setIsSearching(false));
    }, 280);

    return () => clearTimeout(handler);
  }, [searchQuery, partnerId]);

  // Open a collection
  const openCollection = (col) => {
    setCurrentCollection(col);
    setMode("collection");
    setSearchQuery("");
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  };

  // Back button handler
  const handleBack = () => {
    if (mode === "article") {
      if (setSelectedArticle) setSelectedArticle(null);
      if (setIsExpanded) setIsExpanded(false);
      if (currentCollection) {
        setMode("collection");
      } else {
        setMode("home");
      }
    } else if (mode === "collection") {
      setMode("home");
      setCurrentCollection(null);
    } else {
      // mode === 'home'
      if (setIsExpanded) setIsExpanded(false);
      const fallback = getFallbackBackView(setupDetails);
      setView(fallback.view);
      if (setActiveIcon) setActiveIcon(fallback.icon);
    }
  };

  // Feedback handler
  const handleReaction = (type) => {
    if (feedbackGiven || !currentArticle?._id || !partnerId) return;
    setFeedbackGiven(type);
    kbService.addReaction(currentArticle._id, partnerId, type);
  };

  // Format creation / update date
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return "";
    }
  };

  // Estimate reading time in minutes
  const getReadTime = (content) => {
    const raw = typeof content === "string" ? content : content?.text || "";
    const words = raw.replace(/<[^>]*>/g, " ").trim().split(/\s+/).length;
    return Math.max(1, Math.ceil(words / 180));
  };

  // Calculate article counts per collection
  const getCollectionArticleCount = (collectionId) => {
    return allPublishedArticles.filter((art) => {
      const parents = Array.isArray(art.parentCollection)
        ? art.parentCollection
        : [art.parentCollection];
      return parents.some(
        (p) => String(p?._id || p) === String(collectionId)
      );
    }).length;
  };

  // Articles for the current collection
  const currentCollectionArticles = useMemo(() => {
    if (!currentCollection) return [];
    return allPublishedArticles.filter((art) => {
      const parents = Array.isArray(art.parentCollection)
        ? art.parentCollection
        : [art.parentCollection];
      return parents.some(
        (p) => String(p?._id || p) === String(currentCollection._id)
      );
    });
  }, [currentCollection, allPublishedArticles]);

  // Clean and sanitize article HTML body
  const sanitizedArticleHtml = useMemo(() => {
    const raw =
      typeof currentArticle?.content === "string"
        ? currentArticle.content
        : currentArticle?.content?.text || "";

    if (!raw) return "";

    // Configure DOMPurify to allow styling & images
    return DOMPurify.sanitize(raw, {
      ADD_TAGS: ["iframe"],
      ADD_ATTR: [
        "target",
        "rel",
        "data-url",
        "data-preview-width",
        "data-text-color",
        "data-text-alignment",
      ],
    });
  }, [currentArticle]);

  // Intercept image clicks inside article body for zoom modal
  useEffect(() => {
    const el = articleContentRef.current;
    if (!el) return;

    const handleImgClick = (e) => {
      const img = e.target.closest("img");
      if (img && img.src) {
        e.preventDefault();
        setZoomedImage(img.src);
      }
    };

    el.addEventListener("click", handleImgClick);
    return () => el.removeEventListener("click", handleImgClick);
  }, [sanitizedArticleHtml, mode]);

  // Filter top-level collections
  const topLevelCollections = useMemo(() => {
    return collections.filter((c) => !c.parentCollection);
  }, [collections]);

  // Helper to render collection icon
  const renderCollectionIcon = (col, className = "w-5 h-5") => {
    if (col?.iconUrl) {
      return (
        <img
          src={col.iconUrl}
          alt={col.name || "icon"}
          className={`${className} object-contain rounded-md`}
        />
      );
    }
    if (col?.icon && FaIcons[col.icon]) {
      const FaComp = FaIcons[col.icon];
      return <FaComp className={className} style={{ color: primaryColor }} />;
    }
    return <BookOpen className={className} style={{ color: primaryColor }} />;
  };

  return (
    <div
      className="w-full flex flex-col h-full flex-1 min-h-0 transition-colors relative"
      style={{ backgroundColor }}
    >
      {/* --- Top Navigation Header --- */}
      <div
        className="flex items-center justify-between px-4 h-14 border-b border-slate-100 shrink-0 sticky top-0 z-20 backdrop-blur-md bg-white/95"
      >
        <button
          onClick={handleBack}
          className="w-8 h-8 rounded-full flex items-center justify-center text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-all border-none outline-none cursor-pointer"
          title="Back"
        >
          <ChevronLeft className="w-5 h-5 stroke-[2.2]" />
        </button>

        <div className="font-bold text-slate-900 text-sm tracking-tight text-center truncate px-2 flex-1">
          {mode === "home" && "Help Center"}
          {mode === "collection" && (currentCollection?.name || "Collection")}
          {mode === "article" && (currentArticle?.title || "Article")}
        </div>

        <div className="flex items-center gap-1 min-w-[32px] justify-end">
          {setIsExpanded && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="w-8 h-8 rounded-full flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-all border-none outline-none cursor-pointer"
              title={isExpanded ? "Collapse" : "Expand"}
            >
              {isExpanded ? (
                <Minimize2 className="w-4 h-4 text-slate-500 hover:text-slate-800" />
              ) : (
                <Maximize2 className="w-4 h-4 text-slate-500 hover:text-slate-800" />
              )}
            </button>
          )}
          <button
            onClick={() => {
              if (setIsOpen) setIsOpen(false);
            }}
            className="w-8 h-8 rounded-full flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-all border-none outline-none cursor-pointer"
            title="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* --- Scrollable Body --- */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-4 pt-3 pb-24 space-y-4"
      >
        {/* ========================================================= */}
        {/* VIEW MODE 1: ARTICLE READER VIEW                          */}
        {/* ========================================================= */}
        {mode === "article" && (
          <div className="space-y-4 animate-in fade-in duration-200">
            {/* Breadcrumb Navigation */}
            <div className="flex items-center gap-1.5 text-xs text-slate-500 flex-wrap pt-1">
              <button
                onClick={() => {
                  setMode("home");
                  if (setSelectedArticle) setSelectedArticle(null);
                }}
                className="hover:text-blue-600 transition-colors font-medium border-none bg-transparent p-0 cursor-pointer"
              >
                Help Center
              </button>
              {currentCollection && (
                <>
                  <ChevronRight className="w-3 h-3 text-slate-300 shrink-0" />
                  <button
                    onClick={() => {
                      setMode("collection");
                      if (setSelectedArticle) setSelectedArticle(null);
                    }}
                    className="hover:text-blue-600 transition-colors font-medium border-none bg-transparent p-0 cursor-pointer truncate max-w-[140px]"
                  >
                    {currentCollection.name}
                  </button>
                </>
              )}
            </div>

            {articleLoading ? (
              <div className="py-12 text-center text-slate-400">
                <div className="w-7 h-7 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                <span className="text-xs">Loading article...</span>
              </div>
            ) : currentArticle ? (
              <>
                {/* Article Header */}
                <div className="border-b border-slate-100 pb-4">
                  <h1 className="text-lg md:text-xl font-bold text-slate-900 leading-snug">
                    {currentArticle.title}
                  </h1>

                  {/* Metadata: Author, Date, Read Time, Views */}
                  <div className="flex items-center gap-3.5 mt-2.5 text-[11px] text-slate-500 flex-wrap font-medium">
                    {currentArticle.writer && (
                      <span className="flex items-center gap-1">
                        <User className="w-3.5 h-3.5 text-slate-400" />
                        <span>{currentArticle.writer}</span>
                      </span>
                    )}
                    {currentArticle.createdAt && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5 text-slate-400" />
                        <span>{formatDate(currentArticle.createdAt)}</span>
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5 text-slate-400" />
                      <span>{getReadTime(currentArticle.content)} min read</span>
                    </span>
                    {typeof currentArticle.viewCount === "number" && currentArticle.viewCount > 0 && (
                      <span className="flex items-center gap-1">
                        <Eye className="w-3.5 h-3.5 text-slate-400" />
                        <span>{currentArticle.viewCount} views</span>
                      </span>
                    )}
                  </div>
                </div>

                {/* Article Content Body */}
                <div
                  ref={articleContentRef}
                  className="article-body text-slate-800 text-xs leading-relaxed space-y-3 break-words overflow-hidden"
                >
                  <style>{`
                    .article-body {
                      font-size: 13px;
                      line-height: 1.7;
                      color: #334155;
                    }
                    .article-body h1 {
                      font-size: 17px;
                      font-weight: 700;
                      color: #0f172a;
                      margin-top: 16px;
                      margin-bottom: 8px;
                      line-height: 1.35;
                    }
                    .article-body h2 {
                      font-size: 15px;
                      font-weight: 700;
                      color: #0f172a;
                      margin-top: 14px;
                      margin-bottom: 6px;
                      line-height: 1.35;
                    }
                    .article-body h3 {
                      font-size: 14px;
                      font-weight: 600;
                      color: #1e293b;
                      margin-top: 12px;
                      margin-bottom: 4px;
                    }
                    .article-body p {
                      margin-bottom: 10px;
                    }
                    .article-body strong {
                      font-weight: 600;
                      color: #0f172a;
                    }
                    .article-body img {
                      max-width: 100% !important;
                      height: auto !important;
                      border-radius: 8px;
                      margin: 10px 0;
                      cursor: zoom-in;
                      border: 1px solid #f1f5f9;
                      transition: transform 0.2s ease, opacity 0.2s ease;
                      display: block;
                    }
                    .article-body img:hover {
                      opacity: 0.92;
                    }
                    .article-body ul, .article-body ol {
                      padding-left: 20px;
                      margin-bottom: 12px;
                    }
                    .article-body li {
                      margin-bottom: 4px;
                    }
                    .article-body a {
                      color: ${primaryColor};
                      text-decoration: underline;
                      word-break: break-all;
                    }
                    .article-body blockquote {
                      border-left: 3px solid ${primaryColor};
                      padding-left: 12px;
                      margin: 12px 0;
                      color: #64748b;
                      font-style: italic;
                    }
                    .article-body table {
                      width: 100% !important;
                      border-collapse: collapse;
                      margin: 12px 0;
                      font-size: 12px;
                    }
                    .article-body th, .article-body td {
                      border: 1px solid #e2e8f0;
                      padding: 6px 8px;
                      text-align: left;
                    }
                    .article-body th {
                      background-color: #f8fafc;
                      font-weight: 600;
                    }
                    .article-body pre, .article-body code {
                      background-color: #f1f5f9;
                      border-radius: 4px;
                      font-size: 11px;
                      font-family: monospace;
                    }
                    .article-body pre {
                      padding: 8px 12px;
                      overflow-x: auto;
                      margin: 10px 0;
                    }
                    .article-body code {
                      padding: 2px 4px;
                    }
                  `}</style>
                  <div dangerouslySetInnerHTML={{ __html: sanitizedArticleHtml }} />
                </div>

                {/* Helpful Feedback Card */}
                <div className="mt-8 pt-4 border-t border-slate-100 bg-slate-50/70 rounded-2xl p-4 text-center">
                  <div className="text-xs font-bold text-slate-800 mb-1">
                    Was this article helpful?
                  </div>
                  <div className="text-[11px] text-slate-500 mb-3">
                    Your feedback helps us improve our guides.
                  </div>

                  {feedbackGiven ? (
                    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold animate-in fade-in">
                      <span>✓</span>
                      <span>Thank you for your feedback!</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => handleReaction("happy")}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white hover:bg-slate-100 border border-slate-200 text-slate-700 text-xs font-semibold transition-all hover:scale-105 active:scale-95 cursor-pointer"
                      >
                        <ThumbsUp className="w-3.5 h-3.5 text-emerald-600" />
                        <span>Yes</span>
                      </button>
                      <button
                        onClick={() => handleReaction("neutral")}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white hover:bg-slate-100 border border-slate-200 text-slate-700 text-xs font-semibold transition-all hover:scale-105 active:scale-95 cursor-pointer"
                      >
                        <Meh className="w-3.5 h-3.5 text-amber-500" />
                        <span>Somewhat</span>
                      </button>
                      <button
                        onClick={() => handleReaction("sad")}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white hover:bg-slate-100 border border-slate-200 text-slate-700 text-xs font-semibold transition-all hover:scale-105 active:scale-95 cursor-pointer"
                      >
                        <ThumbsDown className="w-3.5 h-3.5 text-rose-500" />
                        <span>No</span>
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="py-12 text-center text-slate-500 text-xs">
                Article not found.
              </div>
            )}
          </div>
        )}

        {/* ========================================================= */}
        {/* VIEW MODE 2: COLLECTION DRILL-DOWN VIEW                   */}
        {/* ========================================================= */}
        {mode === "collection" && currentCollection && (
          <div className="space-y-4 animate-in fade-in duration-200">
            {/* Breadcrumbs */}
            <div className="flex items-center gap-1.5 text-xs text-slate-500 flex-wrap pt-1">
              <button
                onClick={() => setMode("home")}
                className="hover:text-blue-600 transition-colors font-medium border-none bg-transparent p-0 cursor-pointer"
              >
                Help Center
              </button>
              <ChevronRight className="w-3 h-3 text-slate-300 shrink-0" />
              <span className="font-semibold text-slate-800 truncate max-w-[200px]">
                {currentCollection.name}
              </span>
            </div>

            {/* Collection Header Banner */}
            <div
              className="p-4 rounded-2xl border transition-all"
              style={{
                backgroundColor: primaryShade(0.04),
                borderColor: primaryShade(0.15),
              }}
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 bg-white shadow-xs border"
                  style={{ borderColor: primaryShade(0.12) }}
                >
                  {renderCollectionIcon(currentCollection, "w-6 h-6")}
                </div>
                <div className="min-w-0">
                  <h2 className="text-base font-bold text-slate-900 leading-snug truncate">
                    {currentCollection.name}
                  </h2>
                  <div className="text-[11px] font-semibold text-slate-500 mt-0.5">
                    {currentCollectionArticles.length} article
                    {currentCollectionArticles.length === 1 ? "" : "s"}
                  </div>
                </div>
              </div>
              {currentCollection.description && (
                <p className="text-xs text-slate-600 mt-3 leading-relaxed">
                  {currentCollection.description}
                </p>
              )}
            </div>

            {/* Articles List for this Collection */}
            <div className="space-y-2">
              <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider px-1">
                Articles in this collection
              </div>

              {currentCollectionArticles.length > 0 ? (
                <div className="divide-y divide-slate-100 bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-xs">
                  {currentCollectionArticles.map((art) => (
                    <div
                      key={art._id || art.slug}
                      onClick={() => openArticle(art)}
                      className="p-3.5 hover:bg-slate-50/90 cursor-pointer flex items-center justify-between gap-3 transition-colors group"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-bold text-slate-800 group-hover:text-blue-600 transition-colors">
                          {art.title}
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-[10px] text-slate-400">
                          {art.writer && <span>By {art.writer}</span>}
                          {art.createdAt && (
                            <span>• {formatDate(art.createdAt)}</span>
                          )}
                          <span>• {getReadTime(art.content)} min read</span>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-blue-600 group-hover:translate-x-0.5 transition-all shrink-0" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center bg-slate-50/50 rounded-2xl border border-dashed border-slate-200 p-4">
                  <BookOpen className="w-6 h-6 text-slate-300 mx-auto mb-2" />
                  <div className="text-xs font-medium text-slate-500">
                    No articles published in this collection yet.
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ========================================================= */}
        {/* VIEW MODE 3: HELP CENTER HOME / SEARCH                    */}
        {/* ========================================================= */}
        {mode === "home" && (
          <div className="space-y-4 animate-in fade-in duration-200">
            {/* Search Input Bar */}
            <div className="relative">
              <div className="bg-slate-100/80 hover:bg-slate-100 rounded-2xl px-2 py-1 flex items-center gap-2.5 transition-all border border-transparent focus-within:border-slate-300 focus-within:bg-white focus-within:shadow-xs">
                <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <input
                  type="text"
                  placeholder="Search articles, guides, tutorials..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full text-xs text-slate-800 placeholder-slate-400 border-none outline-none bg-transparent"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="text-slate-400 hover:text-slate-600 p-0 border-none bg-transparent cursor-pointer"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>

            {/* If searching, render live search results */}
            {searchQuery.trim().length >= 2 ? (
              <div className="space-y-2">
                <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider px-1 flex items-center justify-between">
                  <span>Search Results</span>
                  {isSearching && (
                    <span className="text-blue-600 lowercase font-normal">
                      Searching...
                    </span>
                  )}
                </div>

                {searchResults.length > 0 ? (
                  <div className="divide-y divide-slate-100 bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-xs">
                    {searchResults.map((art) => (
                      <div
                        key={art._id || art.slug}
                        onClick={() => openArticle(art)}
                        className="p-3.5 hover:bg-blue-50/50 cursor-pointer flex items-center justify-between gap-3 transition-colors group"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-bold text-slate-900 group-hover:text-blue-600 transition-colors">
                            {art.title}
                          </div>
                          {art.collectionId?.name && (
                            <div className="text-[10px] text-slate-400 mt-0.5">
                              In {art.collectionId.name}
                            </div>
                          )}
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-blue-600 group-hover:translate-x-0.5 transition-all shrink-0" />
                      </div>
                    ))}
                  </div>
                ) : !isSearching ? (
                  <div className="py-8 text-center bg-slate-50/60 rounded-2xl border border-dashed border-slate-200 p-4">
                    <Search className="w-6 h-6 text-slate-300 mx-auto mb-2" />
                    <div className="text-xs font-medium text-slate-600">
                      No articles found for "{searchQuery}"
                    </div>
                    <div className="text-[11px] text-slate-400 mt-1">
                      Try searching with different keywords
                    </div>
                  </div>
                ) : null}
              </div>
            ) : (
              <>
                {/* 1. FREQUENTLY READ ARTICLES SECTION (At top, matching LandingPage.jsx) */}
                {frequentlyReadArticles.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 px-1">
                      <div className="w-5 h-5 rounded-md bg-amber-50 border border-amber-200/60 flex items-center justify-center shrink-0">
                        <Star className="w-3 h-3 text-amber-500 fill-amber-400" />
                      </div>
                      <span className="text-xs font-bold text-slate-900">
                        Frequently Read Articles
                      </span>
                    </div>

                    <div className="divide-y divide-slate-100 bg-white rounded-2xl border border-slate-200/80 shadow-xs overflow-hidden">
                      {frequentlyReadArticles.map((art) => (
                        <div
                          key={art._id || art.slug}
                          onClick={() => openArticle(art)}
                          className="p-3 hover:bg-slate-50/90 cursor-pointer flex items-center justify-between gap-3 transition-colors group"
                        >
                          <span className="text-xs font-semibold text-slate-700 group-hover:text-blue-600 transition-colors line-clamp-1 flex-1">
                            {art.title}
                          </span>
                          <ChevronRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-blue-600 group-hover:translate-x-0.5 transition-all shrink-0" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 2. COLLECTIONS SECTION */}
                <div className="space-y-2 pt-1">
                  <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider px-1 flex items-center justify-between">
                    <span>Collections</span>
                    <span>{topLevelCollections.length}</span>
                  </div>

                  {loading ? (
                    <div className="py-8 text-center text-slate-400">
                      <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                      <span className="text-xs">Loading collections...</span>
                    </div>
                  ) : topLevelCollections.length > 0 ? (
                    <div className="divide-y divide-slate-100 bg-white rounded-2xl border border-slate-200/80 shadow-xs overflow-hidden">
                      {topLevelCollections.map((col) => {
                        const count = getCollectionArticleCount(col._id);
                        return (
                          <div
                            key={col._id || col.slug}
                            onClick={() => openCollection(col)}
                            className="group flex items-center gap-3.5 p-3.5 hover:bg-slate-50/90 cursor-pointer transition-all active:scale-[0.99]"
                          >
                            <div
                              className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-colors border"
                              style={{
                                backgroundColor: primaryShade(0.06),
                                borderColor: primaryShade(0.12),
                              }}
                            >
                              {renderCollectionIcon(col, "w-5 h-5")}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="text-xs font-bold text-slate-900 group-hover:text-blue-600 transition-colors truncate">
                                {col.name || "Untitled Collection"}
                              </h4>
                              <p className="text-[11px] text-slate-500 line-clamp-1 mt-0.5 leading-relaxed">
                                {col.description || `Articles about ${col.name}`}
                              </p>
                              {count > 0 && (
                                <div className="text-[10px] text-slate-400 mt-1 font-medium">
                                  {count} article{count === 1 ? "" : "s"}
                                </div>
                              )}
                            </div>
                            <ChevronRight className="w-4 h-4 text-slate-300 shrink-0 group-hover:text-blue-600 group-hover:translate-x-0.5 transition-all" />
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="py-8 text-center bg-slate-50/50 rounded-2xl border border-dashed border-slate-200 p-4">
                      <BookOpen className="w-6 h-6 text-slate-300 mx-auto mb-2" />
                      <div className="text-xs font-medium text-slate-600">
                        No collections available yet.
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* --- Fullscreen Image Zoom Modal --- */}
      {zoomedImage && (
        <div
          onClick={() => setZoomedImage(null)}
          className="fixed inset-0 z-[99999] bg-black/85 backdrop-blur-xs flex items-center justify-center p-4 cursor-zoom-out animate-in fade-in duration-200"
        >
          <button
            onClick={() => setZoomedImage(null)}
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 text-white flex items-center justify-center border-none outline-none cursor-pointer transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <img
            src={zoomedImage}
            alt="Preview"
            className="max-w-full max-h-[90vh] object-contain rounded-xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

export default Help;
